/**
 * SEO component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.org/docs/use-static-query/
 */

import { boolean } from "boolean"
import { useStaticQuery, graphql } from "gatsby"
import React from "react"

interface Meta {
  title?: string
  description?: string
  content?: string
  ogTitle?: string
  image?: string
  ogDescription?: string
  twitterTitle?: string
  twitterDescription?: string
  structuredData?: string
}

interface Props {
  path: string
  children?: React.ReactNode
  lang?: string
  meta?: Meta
  omitDescription?: boolean
  omitKeywords?: boolean
  noIndex?: boolean
}

interface StaticQueryResult {
  site: {
    siteMetadata: {
      siteUrl: string
      title: string
      description: string
      author: string
      image: {
        filename: string
      }
    }
  }
}

const SEO = ({
  path,
  children,
  meta,
  omitDescription,
  noIndex,
}: Props): React.ReactElement => {
  const { site }: StaticQueryResult = useStaticQuery(graphql`
    query {
      site {
        siteMetadata {
          title
          description
          author
          siteUrl
          image {
            filename
          }
        }
      }
    }
  `)

  const siteUrl = site.siteMetadata.siteUrl
  const metaDescription = meta?.description || site.siteMetadata.description
  const metaTitle = meta?.title || site.siteMetadata.title
  const ogTitle = meta?.ogTitle || metaTitle
  const ogDescription = meta?.ogDescription || metaDescription
  const twitterTitle = meta?.twitterTitle || metaTitle
  const twitterDescription = meta?.twitterDescription || metaDescription
  const isBrowser = typeof window !== "undefined"
  const metaImage = meta?.image || site.siteMetadata.image.filename
  const structuredData = meta && meta?.structuredData

  const metaTags = [
    {
      name: `google-site-verification`,
      content: "vUkyCDbz7tLUi-h_eD6f0jTBjT_Pnjv7RwCgus4-iB8",
    },
    {
      name: `google-site-verification`,
      content: "QJa29n-NwdUEqjpnyC5w8v55aXQQwCyLU5ZRILyR73s",
    },
    {
      property: `og:title`,
      content: ogTitle,
    },
    {
      property: `og:type`,
      content: `website`,
    },
    {
      property: `og:image`,
      content: metaImage,
    },
    {
      property: `og:image:width`,
      content: `920`,
    },
    {
      property: `og:image:height`,
      content: `390`,
    },
    {
      name: `twitter:card`,
      content: `summary`,
    },
    {
      name: `twitter:creator`,
      content: site.siteMetadata.author,
    },
    {
      name: `twitter:title`,
      content: twitterTitle,
    },
  ]

  if (!omitDescription) {
    metaTags.push({
      property: `og:description`,
      content: ogDescription,
    })
    metaTags.push({
      name: `description`,
      content: metaDescription,
    })
    metaTags.push({
      name: `twitter:description`,
      content: twitterDescription,
    })
  }

  if (!boolean(process.env.GATSBY_INCLUDE_ROBOTS_TXT)) {
    metaTags.push({
      name: `robots`,
      content: "noindex",
    })
  } else if (noIndex) {
    metaTags.push({
      name: `robots`,
      content: "noindex, nofollow",
    })
  }

  /*
    NOTE: The conditional meta tag addition below is being used to remove
          undesireable iOS behavior of auto-zooming a website to compensate
          for an input's font size [1]. This is a quick fix that should rather
          be addressed by setting the font size of every text input to 16px
          to mitigate the need for this feature and follow best UX practices [2].

          [1]: https://stackoverflow.com/questions/9038625/detect-if-device-is-ios
          [2]: https://designcode.io/ios-design-handbook-design-for-accessibility
  */
  if (
    isBrowser &&
    !window.MSStream &&
    /iPad|iPhone|iPod/.test(navigator.userAgent)
  ) {
    metaTags.push({
      name: `viewport`,
      content: "width=device-width, initial-scale=1, maximum-scale=1",
    })
  }

  // NOTE: Add this meta only on home page
  if (path.endsWith("/za/")) {
    metaTags.push({
      name: "google-site-verification",
      content: "z0Vsz4vfgAjtt1a5gy344SYdBPY9MhE97gVN6enxJxY",
    })
  }

  const formattedStructuredData = structuredData
    ? JSON.stringify(JSON.parse(structuredData))
    : ""

  return (
    <>
      <title id="title">{metaTitle}</title>
      <link rel="canonical" href={`${siteUrl}${path}`} />
      {metaTags.map((tag) => (
        <meta
          key={`${tag.name || tag.property}_${tag.content}`}
          name={tag.name}
          content={tag.content}
          property={tag.property}
        />
      ))}
      {formattedStructuredData !== "" && (
        <script type="application/ld+json">{formattedStructuredData}</script>
      )}
      {children}
    </>
  )
}

export default SEO
